import React, { useState } from 'react';
import API from '../../api/api';
import { MediaType } from '../../api/structs/media';
import { MediaProfile, MediaOutputSpec } from '../../api/structs/media-spec';
import { Modal } from '../modals';
import { Button, TextArea, Select } from '../ui';
import CheckpointProviderSelector from '../selectors/CheckpointProviderSelector';
import ModelSettingsSection from '../selectors/ModelSettingsSection';
import { useToast } from '../../hooks/useToast';
import { LowBalanceBanner } from '../layout';

interface AddMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  clipId: string;
  onSuccess: () => void;
  defaultType: MediaType;
  mediaProfile: MediaProfile;
}

const toSelectorModality = (type: MediaType): 'image' | 'video' | 'audio' => {
  if (type === 'ai_video') return 'video';
  if (type === 'audio') return 'audio';
  return 'image';
};

const AddMediaModal: React.FC<AddMediaModalProps> = ({
  isOpen,
  onClose,
  clipId,
  onSuccess,
  defaultType,
  mediaProfile,
}) => {
  const toast = useToast();
  const [type, setType] = useState<MediaType>(defaultType);
  const [prompt, setPrompt] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [settings, setSettings] = useState<Partial<MediaOutputSpec>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getBaseOutputSpec = (): MediaOutputSpec | undefined => {
    switch (type) {
      case 'image': return mediaProfile.image;
      case 'ai_video': return mediaProfile.video;
      case 'audio': return mediaProfile.audio;
    }
  };

  const buildOutputSpec = (): MediaOutputSpec | undefined => {
    const base = getBaseOutputSpec();
    if (!provider && !model && Object.keys(settings).length === 0) return base;
    return {
      ...(base || {}),
      ...settings,
      provider: provider || base?.provider || '',
      model: model || base?.model || '',
    };
  };

  const handleTypeChange = (nextType: MediaType) => {
    setType(nextType);
    // Reset overrides when type changes
    setProvider('');
    setModel('');
    setSettings({});
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    setIsSubmitting(true);
    try {
      const outputSpec = buildOutputSpec();
      switch (type) {
        case 'image':
          await API.createImage(clipId, prompt, undefined, outputSpec);
          break;
        case 'ai_video':
          await API.createAIVideo(clipId, prompt, undefined, outputSpec);
          break;
        case 'audio':
          await API.createAudio(clipId, prompt, undefined, outputSpec);
          break;
      }
      setPrompt('');
      setProvider('');
      setModel('');
      setSettings({});
      onClose();
      onSuccess();
    } catch (error: any) {
      toast({ text: `Failed: ${error.message}`, level: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolvedSpec = buildOutputSpec();

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} title="Add Scene">
      <div className="space-y-4">
        <LowBalanceBanner />
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1 font-mono">Type</label>
          <Select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as MediaType)}
            options={[
              { value: 'image', label: 'Image' },
              { value: 'ai_video', label: 'AI Video' },
              { value: 'audio', label: 'Audio' },
            ]}
            selectSize="sm"
          />
        </div>

        <TextArea
          label="Prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe what to generate..."
          rows={4}
        />

        <div className="space-y-2 pt-3 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">AI Model</p>
          <CheckpointProviderSelector
            provider={provider}
            model={model}
            modality={toSelectorModality(type)}
            onProviderChange={(value) => {
              setProvider(value);
              setModel('');
              setSettings({});
            }}
            onModelChange={(value) => {
              setModel(value);
              setSettings({});
            }}
          />
        </div>

        <ModelSettingsSection
          modelId={model || getBaseOutputSpec()?.model || ''}
          modality={toSelectorModality(type)}
          settings={settings}
          onSettingsChange={setSettings}
          title="Advanced settings"
          description="Override inherited width, height, duration and any other model-specific fields."
        />

        {resolvedSpec && (
          <div className="text-xs text-zinc-600 bg-zinc-900 border border-white/10 p-2 font-mono">
            {resolvedSpec.provider}/{resolvedSpec.model}
            {resolvedSpec.width && resolvedSpec.height
              ? ` · ${resolvedSpec.width}×${resolvedSpec.height}`
              : ''}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !prompt.trim()}>
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>

    </>
  );
};

export default AddMediaModal;
